import { Application, Color, Container, Graphics, Text } from 'pixi.js';
import {
    BaseWidget,
    WidgetPosition,
} from 'ff/debrief/components/hud/widgets/BaseWidget';
import { CurrentPoint } from 'ff/debrief/models';
import { HudContext } from 'ff/debrief/components/hud/HudContext';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';
import { AirspeedTapeWidget } from 'ff/debrief/components/hud/widgets/AirspeedTapeWidget';
import { RollingNumberWidget } from 'ff/debrief/components/hud/widgets/RollingNumberWidget';

export class AirspeedSection extends BaseWidget {
    static readonly requiredProperties = [
        'indicatedAirSpeed',
        'groundSpeed',
        'trueAirSpeed',
    ] as const;
    static readonly optionalProperties = [] as const;

    // Layout constants
    private static readonly LABEL_PADDING = 5;
    private static readonly VALUE_PADDING = 5;
    private static readonly VALUE_FONT_SIZE_DELTA = 4;
    private static readonly BOX_BORDER_WIDTH = 1;
    private static readonly BOX_BORDER_COLOR = 0xffffff;
    private static readonly BOX_BACKGROUND_COLOR = 0x000000;
    // Extends boxes to the absolute left edge of the HUD viewport (beyond section boundary)
    private static readonly LEFT_EDGE_EXTENSION = 0.042;

    private airspeedTape: AirspeedTapeWidget;
    private currentAirspeed: RollingNumberWidget;
    private tasLabel: Text;
    private tasValue: Text;
    private gsLabel: Text;
    private gsValue: Text;

    // Section layout (0.24 width x 1.0 height): TAS box (0-0.1), IAS tape (0.1-0.9), GS box (0.9-1.0)
    private createSpeedInfoBox(
        container: Container,
        hudContext: HudContext,
        position: WidgetPosition,
        labelText: string,
        valueColor: string | number
    ): { label: Text; value: Text } {
        const background = new Graphics()
            .rect(position.x, position.y, position.width, position.height)
            .fill(AirspeedSection.BOX_BACKGROUND_COLOR)
            .stroke({
                width: AirspeedSection.BOX_BORDER_WIDTH,
                color: AirspeedSection.BOX_BORDER_COLOR,
            });
        container.addChild(background);

        // Create label in white
        const label = new Text({
            text: labelText,
            style: {
                fontFamily: hudContext.globalConfig.fontFamily,
                fontSize: hudContext.globalConfig.baseFontSize,
                fill: 'white',
            },
        });
        label.anchor.set(0, 0.5);
        label.position.set(
            position.x + AirspeedSection.LABEL_PADDING,
            position.y + position.height / 2
        );
        container.addChild(label);

        const value = new Text({
            text: '0',
            style: {
                fontFamily: hudContext.globalConfig.fontFamily,
                fontSize:
                    hudContext.globalConfig.baseFontSize +
                    AirspeedSection.VALUE_FONT_SIZE_DELTA,
                fontWeight: 'bold',
                fill: valueColor,
            },
        });
        value.anchor.set(1, 0.5); // Right-aligned
        value.position.set(
            position.x + position.width - AirspeedSection.VALUE_PADDING,
            position.y + position.height / 2
        );
        container.addChild(value);

        return { label, value };
    }

    override async init(
        pixiApp: Application,
        hudContext: HudContext,
        position: WidgetPosition,
        config?: Record<string, never>
    ): Promise<void> {
        await super.init(pixiApp, hudContext, position, config);

        // Create airspeed tape (aligns exactly with altitude tape)
        // Starts at y=0.1 (below TAS box/heading tape) and extends to y=0.9
        this.airspeedTape = new AirspeedTapeWidget();
        await this.airspeedTape.init(
            pixiApp,
            hudContext,
            this.positionTranslator.translate({
                x: 0,
                y: 0.1,
                width: 1,
                height: 0.8,
            }),
            {},
            this.elementContainer
        );

        // Create current airspeed display (black box)
        this.currentAirspeed = new RollingNumberWidget();
        await this.currentAirspeed.init(
            pixiApp,
            hudContext,
            this.positionTranslator.translate({
                x: 0.25,
                y: 0.46,
                width: 0.75,
                height: 0.08,
            }),
            {
                dataProperty: 'indicatedAirSpeed',
                precision: 1,
                fontSize: 12,
                fontColor: 'white',
                backgroundColor: '0x00000080',
            },
            this.elementContainer
        );
    }

    override async populateElementContainer(
        elementContainer: Container,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): Promise<void> {
        await this.airspeedTape.populate();
        await this.currentAirspeed.populate();

        // Create TAS (True Airspeed) display
        const tasBox = this.createSpeedInfoBox(
            elementContainer,
            hudContext,
            this.positionTranslator.translate({
                x: -AirspeedSection.LEFT_EDGE_EXTENSION,
                y: 0,
                width: 1 + AirspeedSection.LEFT_EDGE_EXTENSION,
                height: 0.1,
            }),
            'TAS',
            'white'
        );
        this.tasLabel = tasBox.label;
        this.tasValue = tasBox.value;

        // Create ground speed display
        const gsBox = this.createSpeedInfoBox(
            elementContainer,
            hudContext,
            this.positionTranslator.translate({
                x: -AirspeedSection.LEFT_EDGE_EXTENSION,
                y: 0.9,
                width: 1 + AirspeedSection.LEFT_EDGE_EXTENSION,
                height: 0.1,
            }),
            'GS',
            'magenta'
        );
        this.gsLabel = gsBox.label;
        this.gsValue = gsBox.value;

        // Place a semi-transparent background behind the entire section
        const background = new Graphics()
            .rect(
                positionTranslator.x(0),
                positionTranslator.y(0),
                positionTranslator.x(1),
                positionTranslator.y(1)
            )
            .fill(new Color([0.8, 0.8, 0.8, 0.2]));
        elementContainer.addChildAt(background, 0);
    }

    protected updateWidget(point: CurrentPoint, hudContext: HudContext): void {
        this.airspeedTape.update(point, hudContext);
        this.currentAirspeed.update(point, hudContext);
        // Values already converted by HudRenderer
        this.tasValue.text = Math.round(point.trueAirSpeed).toString();
        this.gsValue.text = Math.round(point.groundSpeed).toString();
    }

    cleanUp(): void {
        this.airspeedTape?.destroy();
        this.airspeedTape = null;

        this.currentAirspeed?.destroy();
        this.currentAirspeed = null;

        this.tasLabel?.destroy();
        this.tasLabel = null;

        this.tasValue?.destroy();
        this.tasValue = null;

        this.gsLabel?.destroy();
        this.gsLabel = null;

        this.gsValue?.destroy();
        this.gsValue = null;
    }
}
